import React, { useContext, useState } from "react";
import MainLayouts from "../../Layout/MainLayouts";
import { Link, useNavigate } from "react-router-dom";
import { ReactComponent as PencilSquare } from "bootstrap-icons/icons/pencil-square.svg";
import { ReactComponent as Eye } from "bootstrap-icons/icons/eye.svg";
import { ReactComponent as Trash } from "bootstrap-icons/icons/trash3.svg";
import { ReactComponent as ToggleOn } from "bootstrap-icons/icons/toggle-on.svg";
import { ReactComponent as ToggleOff } from "bootstrap-icons/icons/toggle-off.svg";
import { ReactComponent as KeyFill } from "bootstrap-icons/icons/key-fill.svg";
import Spinner from "../spinner/Spinner";
import { useHeadOffices, useDeleteHeadOffice, useToggleHeadOfficeActivation, useResetHeadOfficePassword } from "../../hooks/useHeadOffice";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { AuthContext } from "../../AuthProvider/AuthProvider";

const HeadOfficeList = () => {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const filters = {};
  if (startDate) filters.start_date = startDate;
  if (endDate) filters.end_date = endDate;

  const { data, isLoading, error } = useHeadOffices(filters);
  const deleteMutation = useDeleteHeadOffice();
  const toggleActivationMutation = useToggleHeadOfficeActivation();
  const resetPasswordMutation = useResetHeadOfficePassword();

  const { permissions } = useContext(AuthContext);

  const headOffices = data?.data || [];
  // console.log("HeadOfficeList - Head Offices:", headOffices);

  const filteredHeadOffices = headOffices.filter((ho) =>
    ho.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ho.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ho.state?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ho.city?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) deleteMutation.mutate(id);
    });
  };

  const handleToggleActivation = (id) => {
    toggleActivationMutation.mutate(id);
  };

  const handleResetPassword = (id) => {
    Swal.fire({
      title: "Reset Password?",
      text: "This will reset the head office's password. They will need to set a new one.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, reset it!",
    }).then((result) => {
      if (result.isConfirmed) resetPasswordMutation.mutate(id);
    });
  };

  const handleDownloadCSV = async () => {
    try {
      const authData = JSON.parse(localStorage.getItem("auth"));
      const token = authData?.token;

      if (!token) {
        toast.error("No access token found");
        return;
      }

      const params = new URLSearchParams({ download: "true", ...filters });
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}admin/head-office?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `head-offices-${new Date().toISOString()}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("CSV downloaded successfully!");
    } catch (error) {
      // console.error("Download error:", error);
      toast.error(error?.message || "Failed to download CSV");
    }
  };

  if (isLoading) return <Spinner />;

  if (error) {
    return (
      <MainLayouts>
        <div className="p-6">
          <div className="text-red-500 bg-red-50 border border-red-200 rounded p-4">
            <h3 className="font-bold mb-2">Error loading head offices</h3>
            <p>{error?.response?.data?.message || error?.message || "An error occurred"}</p>
          </div>
        </div>
      </MainLayouts>
    );
  }

  return (
    <MainLayouts>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Head Offices</h1>
          {permissions.some(permission => permission.name === 'head_office.create') && (
            <Link
              to="/head-office/create"
              className="bg-orange1 text-white px-4 py-2 rounded hover:bg-orange-600 transition"
            >
              Create Head Office
            </Link>
          )}
        </div>

        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <input
              type="text"
              placeholder="Search by name, email, state, or city..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="border border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-orange1"
            />
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="border border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-orange1"
            />
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="border border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-orange1"
            />
            <button
              onClick={handleDownloadCSV}
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition"
            >
              Download CSV
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Phone
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Branches
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Activated
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredHeadOffices.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-4 text-center text-gray-500">
                      No head offices found
                    </td>
                  </tr>
                ) : (
                  filteredHeadOffices.map((headOffice) => (
                    <tr key={headOffice.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex gap-2">
                          <button
                            onClick={() => navigate(`/head-office/details?id=${headOffice.id}`)}
                            className="text-blue-600 hover:text-blue-900"
                            title="View Details"
                          >
                            <Eye className="w-5 h-5" />
                          </button>

                          {permissions.some(permission => permission.name === 'head_office.edit') && (
                            <>
                              <button
                                onClick={() => navigate(`/head-office/edit?id=${headOffice.id}`)}
                                className="text-yellow-600 hover:text-yellow-900"
                                title="Edit"
                              >
                                <PencilSquare className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => handleToggleActivation(headOffice.id)}
                                className={`${headOffice.is_activated ? "text-red-600 hover:text-red-900" : "text-green-600 hover:text-green-900"
                                  }`}
                                title={headOffice.is_activated ? "Deactivate" : "Activate"}
                                disabled={toggleActivationMutation.isLoading}
                              >
                                {headOffice.is_activated ? (
                                  <ToggleOff className="w-5 h-5" />
                                ) : (
                                  <ToggleOn className="w-5 h-5" />
                                )}
                              </button>
                            </>
                          )}
                          {permissions.some(permission => permission.name === 'head_office.reset_password') && (
                            <button
                              onClick={() => handleResetPassword(headOffice.id)}
                              className="text-purple-600 hover:text-purple-900"
                              title="Reset Password"
                              disabled={resetPasswordMutation.isLoading}
                            >
                              <KeyFill className="w-5 h-5" />
                            </button>
                          )}

                          {/* <button
                            onClick={() => handleDelete(headOffice.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete"
                            disabled={deleteMutation.isLoading}
                          >
                            <Trash className="w-5 h-5" />
                          </button> */}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{headOffice.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{headOffice.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{headOffice.phone_number}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          {headOffice.city}, {headOffice.state}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          {headOffice.active_branches}/{headOffice.total_branches}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span style={{
                          padding: "2px 10px",
                          borderRadius: "9999px",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          textTransform: "capitalize",
                          backgroundColor: headOffice.status === "active" ? "#dcfce7" : headOffice.status === "suspended" ? "#fef9c3" : "#fee2e2",
                          color: headOffice.status === "active" ? "#166534" : headOffice.status === "suspended" ? "#854d0e" : "#991b1b",
                        }}>
                          {headOffice.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span style={{
                          padding: "2px 10px",
                          borderRadius: "9999px",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          backgroundColor: headOffice.is_activated ? "#dbeafe" : "#f3f4f6",
                          color: headOffice.is_activated ? "#1e40af" : "#4b5563",
                        }}>
                          {headOffice.is_activated ? "Activated" : "Not Activated"}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </MainLayouts>
  );
};

export default HeadOfficeList;
